import {
	type CustomCellEditorProps,
	DATETIME_HINT,
	DatePicker,
} from '@uturn/ui-kit';
import { isValidDate } from '@uturn/utilities/browser';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { matchLocationField } from '../utils/locations';

type Props = CustomCellEditorProps;

const DateTimePickerEditor: FC<Props> = ({
	value,
	onValueChange,
	api,
	rowIndex,
	column,
	data,
}) => {
	const [date, setDate] = useState<Date | null>(() => {
		if (!value) {
			return null;
		}
		const initialDate = new Date(value);
		return isValidDate(initialDate) ? initialDate : null;
	});

	// For `until` cells, mirror manual create's UX: disable dates before `from`'s
	// start-of-day in the calendar, and clamp any picked datetime to `>= from`.
	const columnId = column.getColId();
	const isUntilField = matchLocationField(columnId, 'until');
	const fromColumnId = isUntilField
		? columnId.replace('_until', '_from')
		: null;
	const fromValue = fromColumnId ? data?.[fromColumnId]?.value : undefined;
	const fromDate = fromValue instanceof Date ? fromValue : null;

	const onChangeHandler = (dateValue: Date | null) => {
		// Ignore invalid dates: see `isValidDate` — storing an Invalid Date
		// crashes the cell renderer (and any other consumer).
		if (dateValue && !isValidDate(dateValue)) {
			return;
		}
		const clampedDate =
			isUntilField && fromDate && dateValue && dateValue < fromDate
				? new Date(fromDate)
				: dateValue;
		setDate(clampedDate);
		onValueChange(clampedDate);
	};

	const stopEditingAndFocus = () => {
		api.stopEditing();
		api.setFocusedCell(rowIndex, column.getColId());
	};

	return (
		<DatePicker
			placeholderText={DATETIME_HINT}
			value={date ?? undefined}
			selected={date ?? undefined}
			defaultOpen={true}
			type="datetime"
			modal={false}
			popoverContentClassName="ag-custom-component-popup"
			onSelect={(selectedDate) => onChangeHandler(selectedDate ?? null)}
			onOpenChange={(isOpen) => {
				if (!isOpen) {
					stopEditingAndFocus();
				}
			}}
			disabled={
				isUntilField && fromDate
					? (calendarDate) =>
							dayjs(calendarDate).isBefore(dayjs(fromDate).startOf('day'))
					: undefined
			}
		/>
	);
};

export default DateTimePickerEditor;
